import { MEALS } from "@/lib/api";

const Cell = ({ value, editable, onChange, testid, placeholder }) =>
  editable ? (
    <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={placeholder}
      data-testid={testid}
      className="w-full rounded-lg border border-transparent bg-transparent p-2 text-[13px] leading-snug outline-none transition-colors hover:border-[#e2e8f0] focus:border-[#3d655b] focus:bg-white" />
  ) : (
    <div className="whitespace-pre-line p-2 text-[13px] leading-snug" data-testid={testid}>{value || <span className="text-slate-300">—</span>}</div>
  );

export function DietPlanTable({ plan, patientName, editable = false, onChange, prefix = "diet" }) {
  const setDay = (i, meal, v) => onChange({ ...plan, days: plan.days.map((d, j) => (j === i ? { ...d, [meal]: v } : d)) });
  const setNote = (meal, v) => onChange({ ...plan, col_notes: { ...plan.col_notes, [meal]: v } });
  const bullets = (plan.general_notes || "").split("\n").map((s) => s.trim()).filter(Boolean);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b-2 border-[#3d655b] pb-4">
        <div>
          <div className="label-xs text-[#3d655b]">Piano Nutrizionale</div>
          <h3 className="mt-1 text-2xl font-extrabold tracking-tight sm:text-3xl" data-testid={`${prefix}-title`}>
            {plan.title || patientName}
          </h3>
        </div>
        {plan.start_date && <div className="text-sm text-slate-500">dal {new Date(`${plan.start_date}T00:00:00`).toLocaleDateString("it-IT")}</div>}
      </div>

      <div className="overflow-x-auto rounded-2xl border border-[#e2e8f0]">
        <table className="w-full min-w-[860px] border-collapse text-left">
          <thead>
            <tr className="bg-[#2b4640] text-white">
              <th className="w-20 px-3 py-3 text-[11px] font-semibold uppercase tracking-[0.14em]">Giorno</th>
              {MEALS.map((m) => (
                <th key={m.key} className="px-3 py-3 text-[11px] font-semibold uppercase tracking-[0.14em]">{m.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {plan.days.map((d, i) => (
              <tr key={i} className="diet-cell border-t border-[#e2e8f0] align-top odd:bg-white even:bg-[#f8fafc]">
                <td className="px-3 py-3">
                  <div className="grid h-9 w-9 place-items-center rounded-full bg-[#edf2ef] text-sm font-extrabold text-[#2f4f47]">{i + 1}</div>
                </td>
                {MEALS.map((m) => (
                  <td key={m.key} className="border-l border-[#e2e8f0]">
                    <Cell value={d[m.key]} editable={editable} onChange={(v) => setDay(i, m.key, v)}
                      testid={`${prefix}-cell-${i + 1}-${m.key}`} placeholder={`${m.label}…`} />
                  </td>
                ))}
              </tr>
            ))}
            <tr className="diet-cell border-t-2 border-[#3d655b] bg-[#edf2ef]/60 align-top">
              <td className="px-3 py-3 text-[11px] font-bold uppercase tracking-[0.14em] text-[#2f4f47]">Note</td>
              {MEALS.map((m) => (
                <td key={m.key} className="border-l border-[#b8956f]/40">
                  <Cell value={plan.col_notes[m.key]} editable={editable} onChange={(v) => setNote(m.key, v)}
                    testid={`${prefix}-note-${m.key}`} placeholder="Note, grammature…" />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="grid gap-6 md:grid-cols-[1fr_auto]">
        <div className="rounded-2xl border border-[#e2e8f0] bg-white p-6">
          <h4 className="mb-3 text-lg font-bold text-[#3d655b]">Note</h4>
          {editable ? (
            <textarea className="field min-h-[160px]" value={plan.general_notes}
              onChange={(e) => onChange({ ...plan, general_notes: e.target.value })}
              placeholder="Una indicazione per riga" data-testid={`${prefix}-general-notes`} />
          ) : (
            <ul className="space-y-2 text-sm" data-testid={`${prefix}-general-notes`}>
              {bullets.map((b, i) => (
                <li key={i} className="flex gap-3"><span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#3d655b]" />{b}</li>
              ))}
            </ul>
          )}
          {editable && <p className="mt-2 text-xs text-slate-400">Ogni riga diventa un punto dell'elenco.</p>}
        </div>
        <div className="flex flex-col items-end justify-end gap-2 text-right text-sm md:min-w-[240px]">
          <img src="/img/logo-mark.png" alt="Logo" className="h-14 w-auto" />
          <div className="font-bold">Dott.ssa Simona Marretta</div>
          <div className="text-slate-500">Biologa Nutrizionista</div>
          <div className="text-slate-500">Specialista in Nutrigenetica</div>
        </div>
      </div>
    </div>
  );
}
